import { useCallback, useRef, useState, useEffect, DragEvent, ChangeEvent } from 'react';
import { Upload, X, Loader2, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { wizardApi } from '@/lib/api';
import { toast } from '@solid/ui';
import type { SubmissionDocument } from '@/types';

interface MultiFileUploadProps {
  submissionId: string;
  documents: SubmissionDocument[];
  onDocumentsChange: (docs: SubmissionDocument[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
}

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
];

const ACCEPTED_EXTENSIONS = '.pdf,.docx,.doc,.txt';

const SUGGESTED_DOC_TYPES = [
  { value: 'BRAND_GUIDELINES', label: 'Brand Guidelines' },
  { value: 'TECHNICAL_SPECS', label: 'Technical Specs' },
  { value: 'SUPPLEMENTARY_RFP', label: 'Supplementary RFP' },
  { value: 'OTHER', label: 'Other Document' },
];

export function MultiFileUpload({
  submissionId,
  documents,
  onDocumentsChange,
  maxFiles = 10,
  maxSizeMB = 20,
}: MultiFileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState<string | null>(null);

  // Use ref to always have latest documents for concurrent uploads
  const documentsRef = useRef(documents);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        return `Invalid file type: ${file.name}. Accepted: PDF, DOCX, TXT`;
      }
      if (file.size > maxSizeBytes) {
        return `File too large: ${file.name}. Maximum size: ${maxSizeMB}MB`;
      }
      return null;
    },
    [maxSizeBytes, maxSizeMB]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const fileKey = `${file.name}-${file.size}`;
      setUploading((prev) => new Set(prev).add(fileKey));

      try {
        // Upload as RFP document type - AI will classify the content
        const doc = await wizardApi.uploadAdditionalDoc(submissionId, file, 'RFP');
        // Use ref to get latest documents (avoids race condition with parallel uploads)
        onDocumentsChange([...documentsRef.current, doc]);
      } catch (err: any) {
        const message = err?.response?.data?.message || `Failed to upload ${file.name}`;
        toast.error(message);
      } finally {
        setUploading((prev) => {
          const next = new Set(prev);
          next.delete(fileKey);
          return next;
        });
      }
    },
    [submissionId, onDocumentsChange]
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remaining = maxFiles - documents.length;

      if (fileArray.length > remaining) {
        toast.error(`You can upload up to ${maxFiles} documents. ${remaining} slots remaining.`);
        return;
      }

      // Validate all files first
      const errors: string[] = [];
      const validFiles: File[] = [];

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          errors.push(error);
        } else {
          validFiles.push(file);
        }
      }

      if (errors.length > 0) {
        toast.error(errors[0]);
      }

      // Upload valid files
      for (const file of validFiles) {
        uploadFile(file);
      }
    },
    [documents.length, maxFiles, validateFile, uploadFile]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (e.dataTransfer.files?.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        handleFiles(e.target.files);
      }
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleRemove = useCallback(
    async (docId: string) => {
      setRemoving(docId);
      try {
        await wizardApi.removeDocument(submissionId, docId);
        onDocumentsChange(documents.filter((d) => d.id !== docId));
      } catch {
        toast.error('Failed to remove document.');
      } finally {
        setRemoving(null);
      }
    },
    [submissionId, documents, onDocumentsChange]
  );

  // For typed document uploads (Brand Guidelines, etc.)
  const typedInputRef = useRef<HTMLInputElement>(null);
  const [pendingType, setPendingType] = useState<string>('OTHER');

  const handleTypedUploadClick = useCallback((docType: string) => {
    setPendingType(docType);
    typedInputRef.current?.click();
  }, []);

  const handleTypedInputChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }

      const fileKey = `${file.name}-${file.size}`;
      setUploading((prev) => new Set(prev).add(fileKey));

      try {
        const doc = await wizardApi.uploadAdditionalDoc(submissionId, file, pendingType);
        onDocumentsChange([...documents, doc]);
      } catch (err: any) {
        const message = err?.response?.data?.message || `Failed to upload ${file.name}`;
        toast.error(message);
      } finally {
        setUploading((prev) => {
          const next = new Set(prev);
          next.delete(fileKey);
          return next;
        });
      }
    },
    [submissionId, documents, onDocumentsChange, pendingType, validateFile]
  );

  const isUploading = uploading.size > 0;
  const canAddMore = documents.length < maxFiles;

  const getDocTypeLabel = (type: string) =>
    SUGGESTED_DOC_TYPES.find((t) => t.value === type)?.label ||
    (type === 'RFP' ? 'RFP Document' : type);

  return (
    <div className="space-y-4">
      {/* Uploaded documents list */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 rounded-xl border border-green-500/20 bg-green-500/5 px-4 py-3"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0 text-green-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">{doc.fileName}</p>
                <p className="text-xs text-slate-400">{getDocTypeLabel(doc.documentType)}</p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors disabled:opacity-50"
                onClick={() => handleRemove(doc.id)}
                disabled={removing === doc.id}
                aria-label={`Remove ${doc.fileName}`}
              >
                {removing === doc.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      {canAddMore && (
        <div
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors duration-200',
            isDragOver
              ? 'border-accent-500 bg-accent-500/10'
              : 'border-slate-600 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70',
            isUploading && 'pointer-events-none opacity-70'
          )}
        >
          {isUploading ? (
            <>
              <Loader2 className="mx-auto h-10 w-10 mb-3 text-accent-400 animate-spin" />
              <p className="text-sm font-medium text-slate-300">Uploading...</p>
            </>
          ) : (
            <>
              <Upload
                className={cn(
                  'mx-auto h-10 w-10 mb-3',
                  isDragOver ? 'text-accent-400' : 'text-slate-400'
                )}
              />
              <p className="text-sm font-medium text-slate-300">
                {documents.length === 0
                  ? 'Drop your RFP documents here or click to browse'
                  : 'Add more documents'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                PDF, DOCX, or TXT (max {maxSizeMB}MB each)
              </p>
              {documents.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">
                  You can upload multiple RFP documents at once
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Main multi-file input for RFPs */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleInputChange}
        className="hidden"
        multiple
        aria-label="Upload documents"
      />

      {/* Typed document input (single file) */}
      <input
        ref={typedInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleTypedInputChange}
        className="hidden"
        aria-label="Upload typed document"
      />

      {/* Additional document type buttons */}
      {canAddMore && documents.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-3">
            Add more documents (optional)
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_DOC_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-slate-500 hover:text-white disabled:opacity-50"
                onClick={() => handleTypedUploadClick(type.value)}
                disabled={isUploading}
              >
                <Upload className="h-3 w-3" />
                {type.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
